/// 3-stop segmented picker for a single voice axis. The Fine-tune
/// disclosure renders three of these (Assertiveness / Responsiveness
/// / Evidence). Stop ids match the `AxisValues` enum stops in
/// `web/src/api.ts`; the api validates them at meeting-create.

export type AxisOption = {
  id: string;
  label: string;
  /** One-line italic description shown under the segmented control
   *  when this stop is selected. Mirrors the design's
   *  `panelDescription`. */
  panelDescription: string;
};

export const ASSERTIVENESS_OPTIONS: AxisOption[] = [
  {
    id: "suggests",
    label: "Suggests",
    panelDescription:
      "Floats options and leaves the decision to the room.",
  },
  {
    id: "recommends",
    label: "Recommends",
    panelDescription:
      "Names a preferred path; signals when it'd push back.",
  },
  {
    id: "directs",
    label: "Directs",
    panelDescription: "Calls the play and pushes back on detours.",
  },
];

export const RESPONSIVENESS_OPTIONS: AxisOption[] = [
  {
    id: "brisk",
    label: "Brisk",
    panelDescription: "Goes straight to the point — no warm-up.",
  },
  {
    id: "balanced",
    label: "Balanced",
    panelDescription:
      "Acknowledges the room briefly before stating the view.",
  },
  {
    id: "warm",
    label: "Warm",
    panelDescription:
      "Mirrors the room's tone, builds rapport before pushing.",
  },
];

export const EVIDENCE_OPTIONS: AxisOption[] = [
  {
    id: "confident",
    label: "Confident",
    panelDescription:
      "States the view without footnotes. Reads like a senior peer.",
  },
  {
    id: "cited",
    label: "Cited",
    panelDescription:
      "Names the source or precedent when it tightens the claim.",
  },
  {
    id: "rigorous",
    label: "Rigorous",
    panelDescription:
      "Always grounds claims in spec, doc, or measured evidence.",
  },
];

export function AxisPicker({
  label,
  hint,
  options,
  value,
  onChange,
  colorToken,
}: {
  label: string;
  hint: string;
  options: AxisOption[];
  value: string;
  onChange: (id: string) => void;
  colorToken: string;
}) {
  const current = options.find((o) => o.id === value) ?? options[1];
  return (
    <div>
      <div style={{ marginBottom: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>
          {label}
        </div>
        <div
          className="dim"
          style={{ fontSize: 11, lineHeight: 1.4 }}
        >
          {hint}
        </div>
      </div>
      <div
        role="radiogroup"
        aria-label={label}
        style={{
          display: "flex",
          padding: 2,
          gap: 2,
          borderRadius: 7,
          background: "var(--card)",
          border: "1px solid var(--rule)",
        }}
      >
        {options.map((o) => {
          const on = value === o.id;
          return (
            <button
              key={o.id}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => onChange(o.id)}
              style={{
                flex: 1,
                padding: "5px 6px",
                fontSize: 11,
                lineHeight: 1.2,
                fontFamily: "inherit",
                cursor: "pointer",
                borderRadius: 5,
                border: "none",
                background: on
                  ? `var(--${colorToken})`
                  : "transparent",
                color: on ? "white" : "var(--ink-2)",
                fontWeight: on ? 600 : 500,
                transition: "background .15s, color .15s",
              }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
      <div
        className="dim"
        style={{
          fontSize: 11,
          marginTop: 6,
          lineHeight: 1.45,
          fontStyle: "italic",
        }}
      >
        {current.panelDescription}
      </div>
    </div>
  );
}
